import type { FC, ReactElement } from "react";
import React, { Children, cloneElement, useCallback, useMemo } from "react";

import cn from "classnames";

import { getDisplayName } from "../../../../../utils/react";

import type { ButtonsViewStackProps } from "../../types";

import classes from "./ButtonsViewStack.styl";

const BUTTONS_VIEW_STACK_TEST_ID = "buttons-view-stack-test-id";
const STACK_DISPLAY_NAME = "ButtonsViewStack";

/** Контейнер для отображения одинаковых компонентов по горизонтали/вертикали и систематизации отступов между ними */
const ButtonsViewStack: FC<ButtonsViewStackProps> = (props) => {
    const {
        view = "horizontal",
        size = "56",
        fillButtons,
        centerButtons,
        customVarsStyles,
        children,
    } = props;

    const updateChildProps = useCallback(
        (childProps: any, displayName: string) => {
            if (displayName === STACK_DISPLAY_NAME) {
                return { fillButtons, ...childProps, size };
            }

            return {
                ...childProps,
                size,
                width: fillButtons ? "100%" : "auto",
            };
        },
        [fillButtons, size],
    );

    const childrenArray = useMemo(() => {
        if (!children) {
            return [];
        }

        return Children.toArray(children).map((child) => {
            const childDisplayName = getDisplayName(child);
            const elem = child as ReactElement;
            const updatedProps = updateChildProps(elem.props, childDisplayName);

            const clonedElem = cloneElement(elem, updatedProps);

            if (childDisplayName === STACK_DISPLAY_NAME) {
                // создаем обертку для ButtonsViewStack, чтобы правильно работали margin
                return (
                    <div
                        key={clonedElem.key}
                        className={classes["buttons-view-stack-wrapper"]}
                    >
                        {clonedElem}
                    </div>
                );
            }
            return clonedElem;
        });
    }, [children, updateChildProps]);

    const stackContainerClassName = cn(
        classes["buttons-view-stack"],
        customVarsStyles,
        {
            [classes["__vertical"]]: view === "vertical",
            [classes[`__size-${size}`]]: size,
            [classes["__center"]]: centerButtons,
        },
    );

    return (
        <div
            className={stackContainerClassName}
            data-tsid={BUTTONS_VIEW_STACK_TEST_ID}
        >
            {childrenArray}
        </div>
    );
};

ButtonsViewStack.displayName = STACK_DISPLAY_NAME;

export default ButtonsViewStack;
